import React, { useEffect, useState } from "react";
import { GuestsModal, Guest } from "../modals/GuestsModal";
import { DateRangeModal } from "../modals/DateRangeModal";
import { bankTransferPayment } from "@services/krent.service";
import { toast } from "react-toastify";

type BankDetails = {
  bankName: string;
  accountNumber: string;
  accountName: string;
};

interface BookingSummaryCardProps {
  propertyImage?: string;
  propertyName?: string;
  propertyRef?: string;
  rating?: number;
  reviewCount?: number;

  dateFrom: Date | null;
  dateTo: Date | null;
  onChangeDates?: (dateFrom: Date | null, dateTo: Date | null) => void;

  // Guests
  guests: Guest;
  onChangeGuests?: (guests: Guest) => void;

  // Property info for modal
  propertyId?: string;
  maxGuests?: number;
  maxPets?: number;
  normalPrice?: number;
  partyPrice?: string | number;
  isPartyPrice?: boolean;
  propertyType?: "shortlet" | "long lease" | "sale";

  // Pricing
  pricePerNight: number;
  numberOfDays: number;
  cautionFee?: number;
  serviceFee?: number;
  currency?: string;

  // Actions
  onPayNow?: () => void;
  onPayWithTransfer?: (receipt: File, remarks: string) => void;
  payNowDisabled?: boolean;
  loading?: boolean;
  transferLoading?: boolean;
  availabilityMessage?: string | null;
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  } | null;

  // Payment flow
  transactionId?: string;
  guestDetails?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    whatsapp: string;
    specialRequests: string;
    termsAccepted: boolean;
  };

  // Calendar props
  intent?: string;

  // Terms and conditions (when form is hidden)
  showTermsCheckbox?: boolean;
  termsAccepted?: boolean;
  onTermsChange?: (accepted: boolean) => void;
}

const formatCompact = (amount: number, currency: string = "₦"): string => {
  if (amount >= 1_000_000) {
    const val = amount / 1_000_000;
    return `${currency}${val % 1 === 0 ? val : val.toFixed(2)}M`;
  }
  if (amount >= 1_000) {
    const val = amount / 1_000;
    return `${currency}${val % 1 === 0 ? val : val.toFixed(1)}k`;
  }
  return `${currency}${amount.toLocaleString()}`;
};

const formatDateRange = (from: Date | null, to: Date | null): string => {
  if (!from || !to) return "Select dates";

  // Convert to Date objects if they're not already
  const fromDate = from instanceof Date ? from : new Date(from);
  const toDate = to instanceof Date ? to : new Date(to);

  // Check if dates are valid
  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
    return "Select dates";
  }

  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const year = toDate.getFullYear();
  const fromStr = fromDate.toLocaleDateString("en-US", opts);

  if (fromDate.getFullYear() === year) {
    const toOpts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const toStr = toDate.toLocaleDateString("en-US", toOpts);
    return `${fromStr} – ${toStr}`;
  } else {
    const fromOpts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const toOpts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const fromStr = fromDate.toLocaleDateString("en-US", fromOpts);
    const toStr = toDate.toLocaleDateString("en-US", toOpts);
    return `${fromStr} – ${toStr}`;
  }
};

const BookingSummaryCard: React.FC<BookingSummaryCardProps> = ({
  propertyImage,
  propertyName,
  propertyRef,
  rating,
  reviewCount,
  dateFrom,
  dateTo,
  onChangeDates,
  guests,
  onChangeGuests,
  propertyId,
  maxGuests = 10,
  maxPets = 0,
  normalPrice = 0,
  partyPrice,
  isPartyPrice = false,
  propertyType = "shortlet",
  pricePerNight,
  numberOfDays,
  cautionFee = 0,
  serviceFee = 0,
  currency,
  onPayNow,
  onPayWithTransfer,
  payNowDisabled = false,
  loading = false,
  transferLoading = false,
  availabilityMessage = null,
  bankDetails: bankDetailsProp = null,
  showTermsCheckbox = false,
  termsAccepted = false,
  onTermsChange,
}) => {
  const [showDateModal, setShowDateModal] = useState(false);
  const [showGuestsModal, setShowGuestsModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    "paystack" | "transfer" | null
  >(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [transferRemarks, setTransferRemarks] = useState("");
  const [fetchedBankDetails, setFetchedBankDetails] =
    useState<BankDetails | null>(null);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsError, setBankDetailsError] = useState<string | null>(null);
  const bankDetails = bankDetailsProp || fetchedBankDetails;
  const subtotal = pricePerNight * numberOfDays;
  const total = subtotal + cautionFee + serviceFee;

  const fetchBankDetails = async () => {
    setBankDetailsLoading(true);
    setBankDetailsError(null);
    try {
      const resp = await bankTransferPayment();
      if (resp?.success && resp?.data) {
        setFetchedBankDetails({
          bankName: resp.data.bankName || "",
          accountNumber: resp.data.accountNumber || "",
          accountName: resp.data.accountName || "",
        });
      } else {
        const message =
          resp?.errors?.[0]?.message || "Could not load bank details.";
        setBankDetailsError(message);
        toast.error(message);
      }
    } catch (error: any) {
      const message =
        error?.errors?.[0]?.message ||
        error?.response?.data?.errors?.[0]?.message ||
        "Could not load bank details.";
      setBankDetailsError(message);
      toast.error(message);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (
      paymentMethod === "transfer" &&
      !bankDetailsProp &&
      !fetchedBankDetails &&
      !bankDetailsLoading
    ) {
      fetchBankDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, bankDetailsProp]);

  const handleReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const clearReceipt = () => {
    if (receiptPreview) URL.revokeObjectURL(receiptPreview);
    setReceiptFile(null);
    setReceiptPreview(null);
  };

  const handleTransferSubmit = () => {
    if (!receiptFile) return;
    if (onPayWithTransfer) {
      onPayWithTransfer(receiptFile, transferRemarks);
    }
  };

  const copyAccountNumber = async () => {
    if (!bankDetails?.accountNumber) return;
    try {
      await navigator.clipboard.writeText(bankDetails.accountNumber);
      toast.success("Account number copied");
    } catch {
      toast.error("Could not copy account number");
    }
  };

  // const handleDateChangeClick = () => {
  //   setShowDateModal(true);
  // };

  // const handleGuestsChangeClick = () => {
  //   setShowGuestsModal(true);
  // };

  const handleDatesChange = (
    newDateFrom: Date | null,
    newDateTo: Date | null,
  ) => {
    if (onChangeDates) {
      onChangeDates(newDateFrom, newDateTo);
    }
  };

  const handleGuestsChange = (newGuests: Guest) => {
    if (onChangeGuests) {
      onChangeGuests(newGuests);
    }
  };

  const dummyImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f0f0f0' width='400' height='300'/%3E%3Ctext fill='%23999' font-family='Arial' font-size='18' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image Available%3C/text%3E%3C/svg%3E";

  return (
    <>
      <div
        className="bg-white rounded-4 p-4"
        style={{
          boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
          maxWidth: "420px",
          width: "100%",
        }}
      >
        {/* Property Info */}
        <div className="d-flex align-items-start gap-3 mb-4">
          <img
            src={propertyImage || dummyImage}
            alt={propertyName}
            className="rounded-3 flex-shrink-0"
            style={{ width: "90px", height: "90px", objectFit: "cover" }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = dummyImage;
            }}
          />
          <div>
            <h5
              className="fw-bold mb-1 font-onest"
              style={{ fontSize: "18px", color: "#111" }}
            >
              {propertyName}
            </h5>
            <span
              className="d-block mb-2 font-onest"
              style={{
                fontSize: "14px",
                color: "#555",
              }}
            >
              Ref: {propertyRef}
            </span>
            <div className="d-flex align-items-center gap-1">
              <span style={{ color: "#111", fontSize: "14px" }}>★</span>
              <span
                className="fw-semibold font-onest"
                style={{ fontSize: "14px", color: "#111" }}
              >
                {rating?.toFixed(2)}
              </span>
              <span
                className="font-onest"
                style={{ fontSize: "14px", color: "#6c757d" }}
              >
                ({reviewCount})
              </span>
            </div>
          </div>
        </div>

        <hr className="my-0" style={{ borderColor: "#c4c4c5" }} />

        {/* Dates */}
        <div className="py-2" style={{ borderBottom: "1px solid #e9ecef" }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span
              className="fw-bold font-onest"
              style={{ fontSize: "16px", color: "#111" }}
            >
              Dates
            </span>
            {/* <button
              className="btn btn-light fw-semibold font-onest"
              style={{
                fontSize: "14px",
                border: "1px solid #dee2e6",
                borderRadius: "10px",
                padding: "6px 18px",
                color: "#111",
              }}
              onClick={handleDateChangeClick}
            >
              Change
            </button> */}
          </div>
          <p
            className="mb-0 font-onest"
            style={{ fontSize: "15px", color: "#333" }}
          >
            {formatDateRange(dateFrom, dateTo)}
          </p>
        </div>

        {/* Guests */}
        <div className="py-2" style={{ borderBottom: "1px solid #e9ecef" }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span
              className="fw-bold font-onest"
              style={{ fontSize: "16px", color: "#111" }}
            >
              Guests
            </span>
            {/* <button
              className="btn btn-light fw-semibold font-onest"
              style={{
                fontSize: "14px",
                border: "1px solid #dee2e6",
                borderRadius: "10px",
                padding: "6px 18px",
                color: "#111",
              }}
              onClick={handleGuestsChangeClick}
            >
              Change
            </button> */}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4px 24px",
              fontSize: "15px",
              color: "#333",
            }}
            className="font-onest w-60"
          >
            {guests.adults > 0 && (
              <span>
                {guests.adults} adult{guests.adults !== 1 ? "s" : ""}
              </span>
            )}
            {guests.infants > 0 && (
              <span>
                {guests.infants} infant{guests.infants !== 1 ? "s" : ""}
              </span>
            )}
            {guests.children > 0 && (
              <span>
                {guests.children} child{guests.children !== 1 ? "ren" : ""}
              </span>
            )}
            {guests.pets > 0 && (
              <span>
                {guests.pets} pet{guests.pets !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        <div className="py-2" style={{ borderBottom: "1px solid #e9ecef" }}>
          <p
            className="fw-bold mb-2 font-onest"
            style={{ fontSize: "16px", color: "#111" }}
          >
            Price details
          </p>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span
              className="font-onest"
              style={{ fontSize: "15px", color: "#333" }}
            >
              {formatCompact(pricePerNight, currency)} x {numberOfDays} day
              {numberOfDays !== 1 ? "s" : ""}
            </span>
            <span
              className="font-onest"
              style={{ fontSize: "15px", color: "#333" }}
            >
              {formatCompact(subtotal, currency)}
            </span>
          </div>

          {cautionFee > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span
                className="font-onest"
                style={{ fontSize: "15px", color: "#6c757d" }}
              >
                Caution fee
              </span>
              <span
                className="font-onest"
                style={{ fontSize: "15px", color: "#6c757d" }}
              >
                {formatCompact(cautionFee, currency)}
              </span>
            </div>
          )}

          {serviceFee > 0 && (
            <div className="d-flex justify-content-between align-items-center">
              <span
                className="font-onest"
                style={{ fontSize: "15px", color: "#6c757d" }}
              >
                Service fee
              </span>
              <span
                className="font-onest"
                style={{ fontSize: "15px", color: "#6c757d" }}
              >
                {formatCompact(serviceFee, currency)}
              </span>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="d-flex justify-content-between align-items-center py-4">
          <span
            className="fw-bold font-onest"
            style={{ fontSize: "16px", color: "#111" }}
          >
            Total ({currency || "NGN"})
          </span>
          <span
            className="fw-bold font-onest"
            style={{ fontSize: "18px", color: "#111" }}
          >
            {formatCompact(total, currency)}
          </span>
        </div>

        {availabilityMessage && (
          <div
            className="mb-3 px-3 py-2 rounded-3 font-onest"
            style={{
              fontSize: "14px",
              color: "#842029",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c2c7",
            }}
          >
            {availabilityMessage}
          </div>
        )}

        {showTermsCheckbox && (
          <div className="border rounded p-3 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="terms-summary"
                checked={termsAccepted}
                onChange={(e) => {
                  if (onTermsChange) {
                    onTermsChange(e.target.checked);
                  }
                  if (!e.target.checked) {
                    setPaymentMethod(null);
                  }
                }}
              />
              <label
                className="form-check-label font-onest"
                htmlFor="terms-summary"
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-primary fw-semibold"
                >
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/cancellation-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none text-primary fw-semibold"
                >
                  Cancellation Policy
                </a>
              </label>
            </div>
            {!termsAccepted && (
              <small className="text-danger font-onest mt-2 d-block">
                Please check this box to agree before you can select a payment
                method
              </small>
            )}
          </div>
        )}

        {/* Payment method */}
        <div className="mb-3">
          <p
            className="fw-semibold font-onest mb-2"
            style={{ fontSize: "15px", color: "#111" }}
          >
            Choose payment method
          </p>

          {showTermsCheckbox && !termsAccepted && (
            <p
              className="font-onest mb-2"
              style={{ fontSize: "13px", color: "#6c757d" }}
            >
              Agree to the terms above to unlock payment options
            </p>
          )}

          <div className="d-flex flex-column gap-2">
            <button
              type="button"
              className="w-100 text-start font-onest"
              disabled={payNowDisabled || loading || transferLoading}
              onClick={() => setPaymentMethod("paystack")}
              style={{
                backgroundColor:
                  paymentMethod === "paystack" ? "#eaf8f5" : "#fff",
                border:
                  paymentMethod === "paystack"
                    ? "1.5px solid #47BBA9"
                    : "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "12px 14px",
                cursor:
                  payNowDisabled || loading || transferLoading
                    ? "not-allowed"
                    : "pointer",
                opacity: payNowDisabled || loading || transferLoading ? 0.65 : 1,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 18,
                    height: 18,
                    border:
                      paymentMethod === "paystack"
                        ? "5px solid #47BBA9"
                        : "1.5px solid #c4c4c5",
                  }}
                />
                <div>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "15px", color: "#111" }}
                  >
                    Pay with Paystack
                  </div>
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    Card, USSD, or bank transfer via Paystack
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              className="w-100 text-start font-onest"
              disabled={payNowDisabled || loading || transferLoading}
              onClick={() => setPaymentMethod("transfer")}
              style={{
                backgroundColor:
                  paymentMethod === "transfer" ? "#eaf8f5" : "#fff",
                border:
                  paymentMethod === "transfer"
                    ? "1.5px solid #47BBA9"
                    : "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "12px 14px",
                cursor:
                  payNowDisabled || loading || transferLoading
                    ? "not-allowed"
                    : "pointer",
                opacity: payNowDisabled || loading || transferLoading ? 0.65 : 1,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 18,
                    height: 18,
                    border:
                      paymentMethod === "transfer"
                        ? "5px solid #47BBA9"
                        : "1.5px solid #c4c4c5",
                  }}
                />
                <div>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "15px", color: "#111" }}
                  >
                    Pay with Transfer
                  </div>
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    Transfer to our account and upload your receipt
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {paymentMethod === "paystack" && (
          <div className="mb-3">
            <div
              className="d-flex gap-2 px-3 py-2 rounded-3 font-onest mb-3"
              style={{
                fontSize: "13px",
                color: "#664d03",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffecb5",
              }}
            >
              <i className="fa-solid fa-circle-info mt-1 flex-shrink-0"></i>
              <span>
                Paystack will deduct a small percentage fee (similar to a tax)
                on the Paystack payment screen. The amount shown above is your
                booking total before that fee.
              </span>
            </div>

            <button
              className="btn w-100 fw-semibold font-onest"
              style={{
                backgroundColor:
                  payNowDisabled || loading ? "#e9ecef" : "#47BBA9",
                color: payNowDisabled || loading ? "#adb5bd" : "#fff",
                borderRadius: "12px",
                padding: "14px",
                fontSize: "16px",
                border: "none",
                cursor: payNowDisabled || loading ? "not-allowed" : "pointer",
              }}
              onClick={() => onPayNow?.()}
              disabled={payNowDisabled || loading}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              Continue with Paystack
            </button>
          </div>
        )}

        {paymentMethod === "transfer" && (
          <div className="mb-3">
            <div
              className="rounded-3 p-3 mb-3 font-onest"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #e9ecef",
              }}
            >
              <p
                className="fw-semibold mb-2"
                style={{ fontSize: "14px", color: "#111" }}
              >
                Transfer details
              </p>

              {bankDetailsLoading && (
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ fontSize: "13px", color: "#6c757d" }}
                >
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Loading bank details...
                </div>
              )}

              {!bankDetailsLoading && bankDetailsError && !bankDetails && (
                <div>
                  <p
                    className="mb-2"
                    style={{ fontSize: "13px", color: "#842029" }}
                  >
                    {bankDetailsError}
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm font-onest"
                    onClick={fetchBankDetails}
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      color: "#111",
                    }}
                  >
                    Try again
                  </button>
                </div>
              )}

              {!bankDetailsLoading && bankDetails && (
                <>
                  <div
                    className="d-flex justify-content-between mb-1"
                    style={{ fontSize: "13px" }}
                  >
                    <span style={{ color: "#6c757d" }}>Bank</span>
                    <span className="fw-semibold" style={{ color: "#111" }}>
                      {bankDetails.bankName}
                    </span>
                  </div>
                  <div
                    className="d-flex justify-content-between align-items-center mb-1"
                    style={{ fontSize: "13px" }}
                  >
                    <span style={{ color: "#6c757d" }}>Account number</span>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1"
                      onClick={copyAccountNumber}
                      style={{ fontSize: "13px" }}
                    >
                      <span className="fw-semibold" style={{ color: "#111" }}>
                        {bankDetails.accountNumber}
                      </span>
                      <i
                        className="fa-regular fa-copy"
                        style={{ color: "#47BBA9", fontSize: "12px" }}
                      ></i>
                    </button>
                  </div>
                  <div
                    className="d-flex justify-content-between mb-2"
                    style={{ fontSize: "13px" }}
                  >
                    <span style={{ color: "#6c757d" }}>Account name</span>
                    <span className="fw-semibold" style={{ color: "#111" }}>
                      {bankDetails.accountName}
                    </span>
                  </div>
                  <p className="mb-0" style={{ fontSize: "12px", color: "#6c757d" }}>
                    Transfer the exact total of{" "}
                    <strong style={{ color: "#111" }}>
                      {formatCompact(total, currency)}
                    </strong>
                    , then upload your receipt below.
                  </p>
                </>
              )}
            </div>

            <div
              className="rounded-3 p-3 mb-3 text-center font-onest"
              style={{
                border: "1.5px dashed #c4c4c5",
                backgroundColor: "#fff",
                opacity: bankDetails ? 1 : 0.6,
                pointerEvents: bankDetails ? "auto" : "none",
              }}
            >
              <input
                type="file"
                id="booking-transfer-receipt"
                className="d-none"
                accept="image/*,.pdf"
                onChange={handleReceiptChange}
              />
              {!receiptFile ? (
                <label
                  htmlFor="booking-transfer-receipt"
                  className="d-block mb-0"
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className="fa-solid fa-cloud-arrow-up d-block mb-2"
                    style={{ fontSize: "22px", color: "#47BBA9" }}
                  ></i>
                  <span
                    className="fw-semibold d-block"
                    style={{ fontSize: "14px", color: "#111" }}
                  >
                    Upload payment receipt
                  </span>
                  <span style={{ fontSize: "12px", color: "#6c757d" }}>
                    Image or PDF · required for transfer payments
                  </span>
                </label>
              ) : (
                <div>
                  {receiptFile.type.startsWith("image/") && receiptPreview ? (
                    <img
                      src={receiptPreview}
                      alt="Receipt preview"
                      className="rounded-3 mb-2"
                      style={{
                        maxHeight: 140,
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <p
                      className="mb-2 fw-semibold"
                      style={{ fontSize: "13px", color: "#111" }}
                    >
                      {receiptFile.name}
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 font-onest"
                    onClick={clearReceipt}
                  >
                    Remove file
                  </button>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label
                className="form-label font-onest mb-1"
                style={{ fontSize: "13px", color: "#555" }}
              >
                Remarks (optional)
              </label>
              <input
                type="text"
                className="form-control font-onest"
                placeholder="e.g. payment reference"
                value={transferRemarks}
                onChange={(e) => setTransferRemarks(e.target.value)}
                style={{
                  borderRadius: "10px",
                  fontSize: "14px",
                  padding: "10px 12px",
                }}
              />
            </div>

            <button
              className="btn w-100 fw-semibold font-onest"
              style={{
                backgroundColor:
                  payNowDisabled ||
                  transferLoading ||
                  !receiptFile ||
                  !bankDetails
                    ? "#e9ecef"
                    : "#47BBA9",
                color:
                  payNowDisabled ||
                  transferLoading ||
                  !receiptFile ||
                  !bankDetails
                    ? "#adb5bd"
                    : "#fff",
                borderRadius: "12px",
                padding: "14px",
                fontSize: "16px",
                border: "none",
                cursor:
                  payNowDisabled ||
                  transferLoading ||
                  !receiptFile ||
                  !bankDetails
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={handleTransferSubmit}
              disabled={
                payNowDisabled ||
                transferLoading ||
                !receiptFile ||
                !bankDetails
              }
            >
              {transferLoading && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              Submit receipt
            </button>
          </div>
        )}

        {!paymentMethod && !(showTermsCheckbox && !termsAccepted) && (
          <p
            className="text-center font-onest mb-0"
            style={{ fontSize: "13px", color: "#6c757d" }}
          >
            Select a payment method to continue
          </p>
        )}
      </div>

      {/* Date Range Modal */}
      {propertyId && (
        <DateRangeModal
          show={showDateModal}
          onClose={() => setShowDateModal(false)}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDatesChange={handleDatesChange}
          propertyId={propertyId}
          normalPrice={normalPrice}
          partyPrice={partyPrice}
          isPartyPrice={isPartyPrice}
          type={propertyType}
        />
      )}

      {/* Guests Modal */}
      <GuestsModal
        show={showGuestsModal}
        onClose={() => setShowGuestsModal(false)}
        guests={guests}
        onGuestsChange={handleGuestsChange}
        maxGuests={maxGuests}
        maxPets={maxPets}
        title="Change guest details"
        subtitle={`This property has a maximum of ${maxGuests} guests, not including infants. ${maxPets > 0 ? ` • ${maxPets} pets allowed` : ""}`}
      />
    </>
  );
};

export default BookingSummaryCard;
